// AF-3.19 P1 — Creator chain frontend orphan gap: Strategy. Real backend
// route (/intelligence/{workspace_id}/creator/strategies, AF-3.3), real
// field names verified directly against creative_intelligence.py's own
// document-construction code this session (strategy_type, strategy_status,
// objective, primary_metric, guardrail_metrics, confidence, quality_score,
// governance_status, approval_status). No client-side scoring or
// recomputation -- this page only renders what the backend returned.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge, GovernanceNote, ApprovalBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creativeStrategyAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorStrategyPage() {
  const ctx = useBrandContext();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creativeStrategyAPI.list(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setStrategies(r.data?.strategies || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-strategy-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Creative Strategy</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">What Martian proposes to test or produce, and why — one strategy per detected opportunity.</p>
        </div>

        <ChainProgress current="Strategy" />

        <BrandContextBar ctx={ctx} />

        {!ctx.ready && !ctx.loadingClients && <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its creative strategies." />}
        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && ctx.ready && strategies.length === 0 && (
          <EmptyState title="No strategy proposed yet" description="Martian has not proposed a creative strategy for this entity yet — strategies are generated from detected opportunities." />
        )}

        {!loading && !error && strategies.length > 0 && (
          <div className="space-y-3" data-testid="creator-strategy-list">
            {strategies.map((s) => (
              <div key={s.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="creator-strategy-row">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{s.strategy_type?.replace(/_/g, ' ')}</p>
                    <p className="text-[13px] text-[var(--text-primary)] font-semibold mt-0.5">{s.core_message || s.test_hypothesis || 'Strategy'}</p>
                  </div>
                  <StatusBadge value={s.strategy_status} />
                </div>
                <div className="flex items-center gap-4 flex-wrap text-[11px] mb-2">
                  <span className="text-[var(--text-secondary)]">Objective: <span className="text-[var(--text-primary)] font-mono">{s.objective || '—'}</span></span>
                  <span className="text-[var(--text-secondary)]">Metric: <span className="text-[var(--text-primary)] font-mono">{s.primary_metric || '—'}</span></span>
                  {s.confidence !== undefined && s.confidence !== null && (
                    <div className="flex items-center gap-1"><span className="text-[var(--text-secondary)]">Confidence:</span><ConfidenceIndicator value={s.confidence} /></div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {s.governance_status && <><GovernanceBadge value={s.governance_status} /></>}
                  <ApprovalBadge value={s.approval_status} />
                </div>
                {s.governance_status && <GovernanceNote value={s.governance_status} />}
                <button
                  data-testid="creator-strategy-evidence-trigger"
                  className="mt-2 text-[11px] text-[var(--accent)]"
                  onClick={() => setEvidenceFor(s)}
                >
                  Why this strategy? →
                </button>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why Martian proposed this strategy"
            evidence={evidenceFor.evidence}
            provenance={{
              source_opportunity_ids: evidenceFor.source_opportunity_ids,
              source_recommendation_ids: evidenceFor.source_recommendation_ids,
              source_diagnosis_ids: evidenceFor.source_diagnosis_ids,
              source_signal_ids: evidenceFor.source_signal_ids,
              source_learning_ids: evidenceFor.source_learning_ids,
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
